import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { TrendingUp, Handshake, Trophy, Crown, Gift, Wallet, Banknote } from 'lucide-react';

const incomeIcons = { roi: TrendingUp, direct: Handshake, level: Trophy, royalty: Crown, reward: Gift };
const incomeColors = { roi: 'var(--primary)', direct: '#22c55e', level: '#3b82f6', royalty: '#a855f7', reward: '#f59e0b' };

export default function Income() {
  const [summary, setSummary] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 15;

  useEffect(() => {
    api.get('/income/summary').then(r => setSummary(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (activeTab !== 'all') params.append('type', activeTab);
    api.get(`/income/my?${params}`).then(r => {
      setIncomes(r.data.incomes);
      setTotal(r.data.total);
    }).catch(console.error).finally(() => setLoading(false));
  }, [activeTab, page]);

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const tabs = ['all', 'roi', 'direct', 'level', 'royalty', 'reward'];
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <DashboardLayout>
      <h1 className="page-title">My Income</h1>
      <p className="page-subtitle">Track all your earnings in one place</p>

      {/* Summary Cards */}
      {summary && (
        <div className="wallet-grid" style={{ marginBottom: 24 }}>
          {[
            { label: 'ROI Wallet', key: 'roiWallet', icon: TrendingUp, color: 'var(--primary)' },
            { label: 'Direct Wallet', key: 'directWallet', icon: Handshake, color: '#22c55e' },
            { label: 'Level Wallet', key: 'levelWallet', icon: Trophy, color: '#3b82f6' },
            { label: 'Royalty Wallet', key: 'royaltyWallet', icon: Crown, color: '#a855f7' },
            { label: 'Reward Wallet', key: 'rewardWallet', icon: Gift, color: '#f59e0b' },
            { label: 'Total Earned', key: 'totalEarned', icon: Wallet, color: '#22c55e' },
          ].map(w => (
            <div key={w.key} className="wallet-card">
              <div style={{ marginBottom: 6, color: w.color, display: 'flex', justifyContent: 'center' }}><w.icon size={24} /></div>
              <div className="wallet-label">{w.label}</div>
              <div className="wallet-value" style={{ color: w.color }}>{formatCurrency(summary[w.key])}</div>
            </div>
          ))}
        </div>
      )}

      {/* Income Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wallet size={18} /> Income History</h3>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Total: {total} records</div>
        </div>

        <div className="tab-bar">
          {tabs.map(t => {
            const TabIcon = t !== 'all' ? incomeIcons[t] : null;
            return (
            <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`}
              onClick={() => { setActiveTab(t); setPage(1); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {TabIcon && <TabIcon size={14} />} {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
            );
          })}
        </div>

        {loading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : incomes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Banknote size={40} /></div>
            <h3>No income records found</h3>
            <p style={{ fontSize: '0.85rem' }}>Activate your package to start earning</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tp-table">
              <thead>
                <tr><th>Date</th><th>Type</th><th>Description</th><th>From</th><th>Level</th><th>Amount</th></tr>
              </thead>
              <tbody>
                {incomes.map(inc => {
                  const RowIcon = incomeIcons[inc.type];
                  return (
                  <tr key={inc._id}>
                    <td style={{ fontSize: '0.85rem' }}>{new Date(inc.createdAt).toLocaleDateString('en-IN')}</td>
                    <td>
                      <span className="badge" style={{ background: `${incomeColors[inc.type]}18`, color: incomeColors[inc.type], border: `1px solid ${incomeColors[inc.type]}40`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <RowIcon size={12} /> {inc.type.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{inc.description || '—'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{inc.fromUser?.name || '—'}</td>
                    <td>{inc.level > 0 ? `L${inc.level}` : '—'}</td>
                    <td style={{ color: '#22c55e', fontWeight: 700 }}>+{formatCurrency(inc.amount)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const p = i + Math.max(1, page - 2);
              if (p > totalPages) return null;
              return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
            })}
            <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}